/**
 * Servicio académico
 * Maneja toda la lógica del sistema académico: objetivos medibles, rúbricas, progreso
 */

import { supabaseService } from './supabaseService';
import { authService } from './authService';
import configService from './configService';
import curriculumData from '../../curriculum_data.json';
import { RUBRIC_WEIGHTS } from '../config/rubricConfig';
import { personalizedTaskService } from './personalizedTaskService';
import { TeacherActivity, ActivityObjective } from '../types';
import webSocketService from './webSocketService';
import { logger } from '../utils/logger';

export interface LessonObjective {
  id: string;
  level: string;
  unit: number;
  lesson: number;
  description: string;
  type?: 'gramatica' | 'vocabulario' | 'comunicativo'; // Tipo de objetivo
  order_index: number;
  created_at?: string;
}

export interface LessonRubric {
  level: string;
  unit: number;
  lesson: number;
  rubric_weights: {
    'Fluidez comunicativa': number;
    'Vocabulario': number;
    'Gramática': number;
    'Pronunciación': number;
  };
}

export interface ObjectiveStatus {
  completed: boolean;
  correct_count: number; // Contador de veces que se ha cumplido el objetivo
  total_correct: number; // Para estadísticas
  completed_at?: string;
  // Para objetivos de vocabulario: rastrear palabras diferentes usadas
  vocabulary_words_used?: string[]; // Array de palabras diferentes ya usadas
  // Rastrear frases ya usadas para evitar repetición
  used_phrases?: string[]; // Array de frases (normalizadas) ya usadas para este objetivo
}

export interface UserLessonProgress {
  id?: string;
  user_email: string;
  level: string;
  unit: number;
  lesson: number;
  objectives_status: Record<string, ObjectiveStatus>;
  personalized_task?: string; // Tarea personalizada generada para esta lección
  task_version?: number; // Número de versión de la tarea (incrementa cada vez que se regenera)
  rubric_evaluation?: any;
  all_objectives_completed: boolean;
  completion_date?: string;
  final_score?: number;
  attempts: number;
  approved_on_attempt?: number; // Número de intento en el que se aprobó (score >= 70)
  last_attempt_date?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AcademicLevel {
  level: string;
  unit?: number;
  lesson?: number;
}

export interface LevelProgress {
  unit: number;
  lesson: number;
  completed: boolean;
  inProgress: boolean;
  unlocked: boolean; // Si la lección está desbloqueada (la anterior está completada)
  completionDate?: string;
  hasEvaluation?: boolean; // Si la lección tiene evaluación completada
}

// Mapeo de niveles base (de Apolearn) a subniveles disponibles
const BASE_LEVEL_TO_SUBLEVELS: Record<string, string[]> = {
  'A1': ['A1.1', 'A1.2'],
  'A2': ['A2.1', 'A2.2', 'A2.3'],
  'B1': ['B1.1', 'B1.2', 'B1.3', 'B1.4', 'B1.5'],
  'B2': ['B2.1', 'B2.2', 'B2.3', 'B2.4', 'B2.5', 'B2.6'],
  'C1': ['C1.A', 'C1.B', 'C1.C', 'C1.D', 'C1.E'],
  'C2': ['C2.A', 'C2.B', 'C2.C', 'C2.D', 'C2.E'],
};

class AcademicService {
  /**
   * Determina el subnivel activo basado en el progreso del usuario
   * Si encuentra un subnivel completado, retorna el siguiente
   * Si no hay progreso previo, retorna el subnivel N.1 (A1.1, A2.1, etc.)
   */
  private async determineActiveSublevel(email: string, baseLevel: string): Promise<string | null> {
    try {
      
      // Obtener subniveles disponibles para este nivel base
      const sublevels = BASE_LEVEL_TO_SUBLEVELS[baseLevel];
      if (!sublevels || sublevels.length === 0) {
        // console.warn('[AcademicService] ⚠️ No hay subniveles definidos para:', baseLevel);
        return null;
      }


      // Obtener el email del usuario desde configService si no se proporciona
      const userEmail = email || configService.getConfig().email;
      
      if (!userEmail) {
        console.warn('[AcademicService] ⚠️ No se puede determinar subnivel: no hay email disponible');
        return sublevels[0]; // Retornar el primero por defecto
      }

      // Verificar progreso de cada subnivel en orden
      let lastCompletedIndex = -1;
      
      for (let i = 0; i < sublevels.length; i++) {
        const sublevel = sublevels[i];
        const isCompleted = await this.checkLevelCompletion(userEmail, sublevel);
        
        
        if (isCompleted) {
          lastCompletedIndex = i;
        } else {
          // Si encontramos uno no completado, no necesitamos verificar los siguientes
          break;
        }
      }

      // Determinar el subnivel activo
      if (lastCompletedIndex === -1) {
        // Ningún subnivel completado, retornar el primero (N.1)
        return sublevels[0];
      } else if (lastCompletedIndex < sublevels.length - 1) {
        // Hay un subnivel completado y hay siguiente disponible
        const nextSublevel = sublevels[lastCompletedIndex + 1];
        return nextSublevel;
      } else {
        // Todos los subniveles están completados, retornar el último
        return sublevels[sublevels.length - 1];
      }
    } catch (error) {
      // console.error('[AcademicService] ❌ Error al determinar subnivel activo:', error);
      // En caso de error, retornar el primero por defecto
      const sublevels = BASE_LEVEL_TO_SUBLEVELS[baseLevel];
      return sublevels && sublevels.length > 0 ? sublevels[0] : null;
    }
  }

  /**
   * Obtiene el nivel académico del usuario desde Apolearn
   */
  async getUserAcademicLevel(): Promise<AcademicLevel | null> {
    try {
      const apolearnData = authService.getApolearnUserData();
      
      if (!apolearnData) {
        console.warn('[AcademicService] ⚠️ No hay datos de Apolearn disponibles');
        return null;
      }
      
      if (!apolearnData.classrooms || apolearnData.classrooms.length === 0) {
        // console.warn('[AcademicService] ⚠️ No hay classrooms en los datos de Apolearn');
        return null;
      }

      // Extraer nivel desde Apolearn
      // Prioridad: 1) levels[].name (nivel base), 2) course_number, 3) nombre de clase
      const classroom = apolearnData.classrooms[0];
      
      // PRIORIDAD 1: Intentar extraer nivel base desde classroom.levels[].name
      if (classroom.levels && Array.isArray(classroom.levels) && classroom.levels.length > 0) {
        const levels = classroom.levels;
        const firstLevel = levels[0];
        const baseLevelName = firstLevel?.name;
        
        logger.log('[AcademicService] 📊 Extrayendo nivel desde classroom.levels:', {
          classroomId: classroom.id,
          classroomName: classroom.name,
          levels: levels,
          firstLevel: firstLevel,
          baseLevelName: baseLevelName
        });
        
        if (baseLevelName) {
          
          // Extraer solo la parte base (A1, A2, B1, B2, C1, C2, etc.) sin el subnivel
          // Manejar tanto niveles numéricos como alfabéticos
          const baseLevelMatch = baseLevelName.match(/^(A\d+|B\d+|C\d+)/i);
          if (baseLevelMatch) {
            const baseLevel = baseLevelMatch[1];
            
            logger.log('[AcademicService] ✅ Nivel base extraído:', {
              baseLevelName: baseLevelName,
              baseLevel: baseLevel
            });
            
            // Obtener email del usuario
            const userEmail = apolearnData.user?.email || configService.getConfig().email;
            
            if (userEmail) {
              // Determinar el subnivel activo basado en el progreso
              const activeSublevel = await this.determineActiveSublevel(userEmail, baseLevel);
              
              if (activeSublevel) {
                return { level: activeSublevel };
              } else {
                console.warn('[AcademicService] ⚠️ No se pudo determinar subnivel activo, usando primero disponible');
                const sublevels = BASE_LEVEL_TO_SUBLEVELS[baseLevel];
                if (sublevels && sublevels.length > 0) {
                  return { level: sublevels[0] };
                }
              }
            } else {
              // console.warn('[AcademicService] ⚠️ No hay email disponible para determinar subnivel');
            }
          } else {
          }
        }
      } else {
      }
      
      // PRIORIDAD 2: Intentar obtener course_number de las estadísticas
      if (classroom.statistics && 'course_number' in classroom.statistics && classroom.statistics.course_number !== undefined && classroom.statistics.course_number !== null) {
        const courseNumber = classroom.statistics.course_number;
        const levelMap: Record<number, string> = {
          1: 'A1.1',
          2: 'A1.2',
          3: 'A2.1',
          4: 'A2.2',
          5: 'A2.3',
        };
        
        const level = levelMap[courseNumber] || 'A1.1';
        return { level };
      } else {
      }

      // PRIORIDAD 3: Intentar extraer nivel del nombre de la clase
      const className = classroom.name || '';
      const levelMatch = className.match(/(A\d\.\d)/i);
      if (levelMatch) {
        const level = levelMatch[1];
        return { level };
      } else {
      }

      // Por defecto, retornar A1.1
      return { level: 'A1.1' };
    } catch (error) {
      console.error('[AcademicService] ❌ Error al obtener nivel académico:', error);
      return null;
    }
  }

  /**
   * Carga objetivos medibles de una lección
   */
  async getLessonObjectives(
    level: string,
    unit: number,
    lesson: number
  ): Promise<LessonObjective[]> {
    try {
      const { data, error } = await supabaseService
        .getClient()
        .from('lesson_objectives')
        .select('*')
        .eq('level', level)
        .eq('unit', unit)
        .eq('lesson', lesson)
        .order('order_index', { ascending: true });

      if (error) {
        // console.error('[AcademicService] Error al cargar objetivos:', error);
        return [];
      }

      return (data || []) as LessonObjective[];
    } catch (error) {
      console.error('[AcademicService] Error al cargar objetivos:', error);
      return [];
    }
  }

  /**
   * Carga rúbrica (pesos) de una lección
   * Ahora retorna pesos fijos desde la configuración
   */
  async getLessonRubric(
    level: string,
    unit: number,
    lesson: number
  ): Promise<LessonRubric | null> {
    try {
      // Retornar pesos fijos desde la configuración
      return {
        level,
        unit,
        lesson,
        rubric_weights: RUBRIC_WEIGHTS,
      };
    } catch (error) {
      console.error('[AcademicService] Error al cargar rúbrica:', error);
      return null;
    }
  }

  /**
   * Obtiene progreso del usuario en una lección
   */
  async getUserLessonProgress(
    email: string,
    level: string,
    unit: number,
    lesson: number
  ): Promise<UserLessonProgress | null> {
    try {
      const { data, error } = await supabaseService
        .getClient()
        .from('user_lesson_progress')
        .select('*')
        .eq('user_email', email)
        .eq('level', level)
        .eq('unit', unit)
        .eq('lesson', lesson)
        .maybeSingle();

      // Error distinto a "no encontrado" (p. ej. varios registros PGRST116)
      if (error) {
        if (error.code === 'PGRST301' || (error as any).status === 406) {
          console.error('[AcademicService] Error 406 al obtener progreso:', {
            code: error.code,
            status: (error as any).status,
            message: error.message,
          });
        }
        return null;
      }

      // Sin fila: crear registro nuevo (evita 406 por .single() con 0 filas)
      if (!data) {
        const objectives = await this.getLessonObjectives(level, unit, lesson);
        const initialStatus: Record<string, ObjectiveStatus> = {};
        objectives.forEach(obj => {
          initialStatus[obj.description] = {
            completed: false,
            correct_count: 0,
            total_correct: 0,
            used_phrases: [],
          };
        });
        const newProgress: Partial<UserLessonProgress> = {
          user_email: email,
          level,
          unit,
          lesson,
          objectives_status: initialStatus,
          all_objectives_completed: false,
          attempts: 0,
        };
        const { data: created, error: createError } = await supabaseService
          .getClient()
          .from('user_lesson_progress')
          .insert(newProgress)
          .select()
          .single();
        if (createError) {
          const isConflict = (createError as any).code === '23505' || (createError as any).status === 409;
          if (isConflict) {
            const { data: existing } = await supabaseService
              .getClient()
              .from('user_lesson_progress')
              .select('*')
              .eq('user_email', email)
              .eq('level', level)
              .eq('unit', unit)
              .eq('lesson', lesson)
              .maybeSingle();
            if (existing) return existing as UserLessonProgress;
          }
          return null;
        }
        return created as UserLessonProgress;
      }

      if (data) {
        // Sincronizar objetivos: asegurar que todos los objetivos actuales estén en objectives_status
        const currentObjectives = await this.getLessonObjectives(level, unit, lesson);
        const currentProgress = data as UserLessonProgress;
        
        // Asegurar que objectives_status existe y es un objeto válido
        if (!currentProgress.objectives_status || typeof currentProgress.objectives_status !== 'object') {
          currentProgress.objectives_status = {};
        }
        
        // Verificar si faltan objetivos nuevos o si los existentes no tienen campos requeridos
        let needsSync = false;
        currentObjectives.forEach((obj) => {
          if (!obj || !obj.description) {
            console.warn('[AcademicService] Objetivo inválido encontrado:', obj);
            return;
          }
          
          const existingStatus = currentProgress.objectives_status?.[obj.description];
          if (!existingStatus) {
            // Agregar objetivo faltante con estado inicial
            currentProgress.objectives_status[obj.description] = {
              completed: false,
              correct_count: 0,
              total_correct: 0,
              used_phrases: [],
            };
            needsSync = true;
          } else if (existingStatus && typeof existingStatus === 'object') {
            // Migrar objetivos existentes que no tengan campos requeridos
            const needsMigration = 
              existingStatus.correct_count === undefined ||
              existingStatus.correct_count === null ||
              existingStatus.total_correct === undefined ||
              existingStatus.total_correct === null ||
              existingStatus.completed === undefined ||
              existingStatus.completed === null;
            
            if (needsMigration) {
              // Asegurar que todos los campos requeridos estén presentes
              const migratedStatus: ObjectiveStatus = {
                completed: existingStatus.completed ?? false,
                correct_count: existingStatus.correct_count ?? 0,
                total_correct: existingStatus.total_correct ?? 0,
                completed_at: existingStatus.completed_at,
                vocabulary_words_used: existingStatus.vocabulary_words_used || [],
                used_phrases: existingStatus.used_phrases || [],
              };
              currentProgress.objectives_status[obj.description] = migratedStatus;
              needsSync = true;
            } else {
              // Asegurar que los arrays opcionales estén inicializados
              if (!existingStatus.used_phrases) {
                existingStatus.used_phrases = [];
                needsSync = true;
              }
              if (!existingStatus.vocabulary_words_used) {
                existingStatus.vocabulary_words_used = [];
                needsSync = true;
              }
            }
          } else {
            // existingStatus existe pero no es un objeto válido, recrearlo
            // console.warn('[AcademicService] Objetivo con estructura inválida, recreando:', obj.description);
            currentProgress.objectives_status[obj.description] = {
              completed: false,
              correct_count: 0,
              total_correct: 0,
              used_phrases: [],
            };
            needsSync = true;
          }
        });

        // Si se agregaron objetivos nuevos, actualizar en la base de datos
        if (needsSync) {
          const { error: syncError } = await supabaseService
            .getClient()
            .from('user_lesson_progress')
            .update({
              objectives_status: currentProgress.objectives_status,
            })
            .eq('user_email', email)
            .eq('level', level)
            .eq('unit', unit)
            .eq('lesson', lesson);

          if (syncError) {
            console.warn('[AcademicService] Error al sincronizar objetivos:', syncError);
          } else {
          }
        }

        const evalValue = currentProgress.rubric_evaluation;
        let evalDetails: any = {
          isNull: evalValue === null,
          isUndefined: evalValue === undefined,
          type: typeof evalValue,
          truthy: !!evalValue
        };
        if (evalValue && typeof evalValue === 'object') {
          evalDetails.keys = Object.keys(evalValue);
          evalDetails.keysCount = Object.keys(evalValue).length;
          evalDetails.isEmpty = Object.keys(evalValue).length === 0;
          evalDetails.hasOverallScore = 'overall_score' in evalValue;
        }

        return currentProgress;
      }

      return data as UserLessonProgress;
    } catch (error) {
      // console.error('[AcademicService] Error al obtener progreso:', error);
      return null;
    }
  }

  /**
   * Actualiza el estado de un objetivo medible
   * Lógica: si es correcto, incrementa contador; si es incorrecto, solo cuenta el error (no reduce el contador)
   */
  async updateObjectiveStatus(
    email: string,
    level: string,
    unit: number,
    lesson: number,
    objectiveDescription: string,
    isCorrect: boolean,
    vocabularyWordsFound?: string[], // Para objetivos de vocabulario: palabras encontradas en el mensaje
    userMessage?: string // Mensaje del usuario para verificar si es repetido
  ): Promise<UserLessonProgress | null> {
    try {
      const progress = await this.getUserLessonProgress(email, level, unit, lesson);
      if (!progress) {
        console.error('[AcademicService] ❌ No se encontró progreso para:', {
          email,
          level,
          unit,
          lesson,
          objectiveDescription
        });
        return null;
      }

      logger.log('[AcademicService] 🔍 Buscando objetivo en progreso:', {
        email,
        level,
        unit,
        lesson,
        objectiveDescription,
        availableObjectives: Object.keys(progress.objectives_status || {}),
        objectivesStatus: progress.objectives_status
      });

      let objectiveStatus = progress.objectives_status?.[objectiveDescription];
      if (!objectiveStatus || typeof objectiveStatus !== 'object') {
        console.error('[AcademicService] ❌ Objetivo no encontrado o inválido:', {
          objectiveDescription,
          availableObjectives: Object.keys(progress.objectives_status || {}),
          objectivesStatus: progress.objectives_status
        });
        return null;
      }

      // Asegurar que el objetivo tenga todos los campos requeridos (migración de datos antiguos)
      let needsMigration = false;
      if (
        objectiveStatus.correct_count === undefined ||
        objectiveStatus.correct_count === null ||
        objectiveStatus.total_correct === undefined ||
        objectiveStatus.total_correct === null ||
        objectiveStatus.completed === undefined ||
        objectiveStatus.completed === null
      ) {
        needsMigration = true;
      }
      
      if (needsMigration) {
        // Migrar objetivo a la estructura actual
        objectiveStatus = {
          completed: objectiveStatus.completed ?? false,
          correct_count: objectiveStatus.correct_count ?? 0,
          total_correct: objectiveStatus.total_correct ?? 0,
          completed_at: objectiveStatus.completed_at,
          vocabulary_words_used: objectiveStatus.vocabulary_words_used || [],
          used_phrases: objectiveStatus.used_phrases || [],
        };
        // Actualizar en el objeto progress
        progress.objectives_status[objectiveDescription] = objectiveStatus;
      } else {
        // Asegurar que los arrays opcionales estén inicializados
        if (!objectiveStatus.used_phrases) {
          objectiveStatus.used_phrases = [];
        }
        if (!objectiveStatus.vocabulary_words_used) {
          objectiveStatus.vocabulary_words_used = [];
        }
      }

      // Actualizar estado según lógica de 3 consecutivas
      // Nota: Este método solo se llama cuando el mensaje está relacionado con el objetivo
      
      // Inicializar arrays de rastreo si no existen
      if (!objectiveStatus.vocabulary_words_used) {
        objectiveStatus.vocabulary_words_used = [];
      }
      if (!objectiveStatus.used_phrases) {
        objectiveStatus.used_phrases = [];
      }
      
      // Agregar frase a la lista de usadas si es correcta
      // NOTA: GPT ya verificó si la frase es repetida antes de marcar isCorrect como true
      // Aquí solo almacenamos la frase si GPT determinó que es nueva y correcta
      if (userMessage && isCorrect) {
        const normalizedMessage = userMessage.toLowerCase().trim();
        
        // Es una frase nueva y correcta (GPT ya verificó que no es repetida) - agregarla a TODOS los objetivos de la lección
        // para que GPT pueda descartarla en evaluaciones futuras
        Object.values(progress.objectives_status).forEach(status => {
          if (!status.used_phrases) {
            status.used_phrases = [];
          }
          if (!status.used_phrases.includes(normalizedMessage)) {
            status.used_phrases.push(normalizedMessage);
          }
        });
      }
      
      // Para objetivos de vocabulario: verificar si las palabras son nuevas
      if (vocabularyWordsFound && vocabularyWordsFound.length > 0) {
        // Filtrar solo palabras nuevas (que no estén en vocabulary_words_used)
        const newWords = vocabularyWordsFound.filter(word => 
          !objectiveStatus.vocabulary_words_used!.includes(word.toLowerCase())
        );
        
        if (newWords.length > 0) {
          // Agregar palabras nuevas al array
          objectiveStatus.vocabulary_words_used.push(...newWords.map(w => w.toLowerCase()));
          // Para vocabulario, correct_count es el número de palabras diferentes usadas
          objectiveStatus.correct_count = objectiveStatus.vocabulary_words_used.length;
          objectiveStatus.total_correct += newWords.length;
          
          // Si alcanza 1 palabra diferente, marcar como completado
          if (objectiveStatus.correct_count >= 1) {
            objectiveStatus.completed = true;
            objectiveStatus.completed_at = new Date().toISOString();
          }
        } else {
          // Todas las palabras ya fueron usadas antes - no cuenta como correcto para vocabulario
          isCorrect = false;
        }
      }
      
      // Lógica estándar para objetivos no-vocabulario o si no hay palabras nuevas
      if (isCorrect && (!vocabularyWordsFound || vocabularyWordsFound.length === 0)) {
        objectiveStatus.correct_count += 1;
        objectiveStatus.total_correct += 1;
        
        // Si alcanza 1 vez, marcar como completado
        if (objectiveStatus.correct_count >= 1) {
          objectiveStatus.completed = true;
          objectiveStatus.completed_at = new Date().toISOString();
        }
      } else if (!isCorrect) {
        // Si no cumple, simplemente no hacer nada
        // No contar errores, no contar intentos, no afectar el progreso
      }

      // Verificar si todos los objetivos están completados
      const allCompleted = Object.values(progress.objectives_status).every(
        obj => obj.completed
      );

      // Actualizar en base de datos
      const { data, error } = await supabaseService
        .getClient()
        .from('user_lesson_progress')
        .update({
          objectives_status: progress.objectives_status,
          all_objectives_completed: allCompleted,
          last_attempt_date: new Date().toISOString(),
          attempts: progress.attempts + 1,
        })
        .eq('user_email', email)
        .eq('level', level)
        .eq('unit', unit)
        .eq('lesson', lesson)
        .select()
        .single();

      if (error) {
        // console.error('[AcademicService] Error al actualizar progreso:', error);
        return null;
      }

      return data as UserLessonProgress;
    } catch (error) {
      console.error('[AcademicService] Error al actualizar progreso:', error);
      return null;
    }
  }

  /**
   * Resetea el progreso de una lección (para reintentar)
   * Mantiene el número de intentos pero resetea objetivos y evaluación
   */
  async resetLessonProgress(
    email: string,
    level: string,
    unit: number,
    lesson: number
  ): Promise<UserLessonProgress | null> {
    try {
      const progress = await this.getUserLessonProgress(email, level, unit, lesson);
      if (!progress) {
        return null;
      }

      // Obtener objetivos para reinicializar el estado
      const objectives = await this.getLessonObjectives(level, unit, lesson);
      const initialStatus: Record<string, ObjectiveStatus> = {};
      
      objectives.forEach(obj => {
        initialStatus[obj.description] = {
          completed: false,
          correct_count: 0,
          total_correct: 0,
          vocabulary_words_used: [], // Resetear palabras de vocabulario
          used_phrases: [], // Resetear frases usadas
        };
      });

      // Actualizar progreso: resetear objetivos y evaluación, mantener intentos
      const { data, error } = await supabaseService
        .getClient()
        .from('user_lesson_progress')
        .update({
          objectives_status: initialStatus,
          all_objectives_completed: false,
          rubric_evaluation: null,
          final_score: null,
          completion_date: null,
          last_attempt_date: new Date().toISOString(),
          // Mantener attempts para historial
        })
        .eq('user_email', email)
        .eq('level', level)
        .eq('unit', unit)
        .eq('lesson', lesson)
        .select()
        .single();

      if (error) {
        // console.error('[AcademicService] Error al resetear progreso:', error);
        return null;
      }

      return data as UserLessonProgress;
    } catch (error) {
      console.error('[AcademicService] Error al resetear progreso:', error);
      return null;
    }
  }

  /**
   * Verifica si todos los objetivos están completados
   */
  async checkAllObjectivesCompleted(
    email: string,
    level: string,
    unit: number,
    lesson: number
  ): Promise<boolean> {
    try {
      const progress = await this.getUserLessonProgress(email, level, unit, lesson);
      if (!progress) {
        return false;
      }

      return progress.all_objectives_completed;
    } catch (error) {
      // console.error('[AcademicService] Error al verificar completitud:', error);
      return false;
    }
  }

  /**
   * Genera actividad conjunta para una lección usando LLM
   * TODO: Implementar generación con LLM
   */
  async generateLessonActivity(
    level: string,
    unit: number,
    lesson: number,
    _userContext?: any
  ): Promise<string> {
    // Por ahora retorna un prompt básico
    // TODO: Integrar con LLM para generar actividad dinámica
    const lessonData = (curriculumData.lessons as any[]).find(
      l => l.level === level && l.unit === unit && l.lesson === lesson
    );

    if (!lessonData) {
      return 'Actividad no encontrada';
    }

    return `Actividad para ${lessonData.description}. Objetivos: ${lessonData.objectives.map((o: any) => o.description).join(', ')}`;
  }

  /**
   * Verifica si completó la lección y desbloquea siguiente
   */
  async checkLessonCompletion(
    email: string,
    level: string,
    unit: number,
    lesson: number
  ): Promise<boolean> {
    try {
      const progress = await this.getUserLessonProgress(email, level, unit, lesson);
      if (!progress || !progress.all_objectives_completed) {
        return false;
      }

      // TODO: Implementar lógica de desbloqueo de siguiente lección
      return true;
    } catch (error) {
      console.error('[AcademicService] Error al verificar completitud de lección:', error);
      return false;
    }
  }

  /**
   * Verifica si completó el nivel completo y sincroniza con Apolearn
   * También detecta cuando se completa un subnivel y registra log de avance
   */
  async checkLevelCompletion(email: string, level: string): Promise<boolean> {
    try {
      // Obtener todas las lecciones del nivel
      const { data: lessons, error } = await supabaseService
        .getClient()
        .from('lesson_objectives')
        .select('level, unit, lesson')
        .eq('level', level)
        .order('unit', { ascending: true })
        .order('lesson', { ascending: true });

      if (error || !lessons) {
        // console.error('[AcademicService] Error al obtener lecciones del nivel:', error);
        return false;
      }

      // Obtener lecciones únicas
      const uniqueLessons = new Set(lessons.map(l => `${l.level}-${l.unit}-${l.lesson}`));
      
      // Verificar que todas las lecciones estén completadas
      for (const lessonKey of uniqueLessons) {
        const [l, u, les] = lessonKey.split('-');
        const progress = await this.getUserLessonProgress(email, l, parseInt(u), parseInt(les));
        
        if (!progress || !progress.all_objectives_completed) {
          return false;
        }
      }

      // Si todas las lecciones están completadas, verificar si hay un siguiente subnivel disponible
      // Manejar tanto niveles numéricos (A1.1, A2.1) como alfabéticos (C1.A, C2.B)
      const levelMatchNumeric = level.match(/^(A\d+|B\d+)\.(\d+)$/);
      const levelMatchAlphabetic = level.match(/^(C\d+)\.([A-E])$/);
      
      let baseLevel: string | null = null;
      if (levelMatchNumeric) {
        baseLevel = levelMatchNumeric[1]; // A1, A2, B1, B2, etc.
      } else if (levelMatchAlphabetic) {
        baseLevel = levelMatchAlphabetic[1]; // C1, C2, etc.
      }
      
      if (baseLevel) {
        const sublevels = BASE_LEVEL_TO_SUBLEVELS[baseLevel];
        if (sublevels && sublevels.length > 0) {
          const currentIndex = sublevels.indexOf(level);
          if (currentIndex >= 0 && currentIndex < sublevels.length - 1) {
            const nextSublevel = sublevels[currentIndex + 1];
          } else if (currentIndex === sublevels.length - 1) {
          }
        }
      }

      // Si todas las lecciones están completadas, verificar en Apolearn si el nivel subió
      const apolearnData = authService.getApolearnUserData();
      if (apolearnData && apolearnData.classrooms && apolearnData.classrooms.length > 0) {
        const classroom = apolearnData.classrooms[0];
        const currentCourseNumber = classroom.statistics?.course_number || 1;
        
        // Si el nivel subió en Apolearn, actualizar
        // TODO: Implementar lógica de sincronización con Apolearn API si es necesario
      }

      return true;
    } catch (error) {
      console.error('[AcademicService] Error al verificar completitud de nivel:', error);
      return false;
    }
  }

  /**
   * Obtiene el progreso de todas las unidades y lecciones de un nivel
   */
  async getLevelProgress(email: string, level: string): Promise<LevelProgress[]> {
    try {
      // Obtener todas las lecciones del nivel desde la base de datos
      const { data: lessons, error } = await supabaseService
        .getClient()
        .from('lesson_objectives')
        .select('level, unit, lesson')
        .eq('level', level)
        .order('unit', { ascending: true })
        .order('lesson', { ascending: true });

      if (error || !lessons) {
        // console.error('[AcademicService] Error al obtener lecciones del nivel:', error);
        return [];
      }

      // Obtener lecciones únicas
      const uniqueLessons = new Map<string, { unit: number; lesson: number }>();
      lessons.forEach(l => {
        const key = `${l.unit}-${l.lesson}`;
        if (!uniqueLessons.has(key)) {
          uniqueLessons.set(key, { unit: l.unit, lesson: l.lesson });
        }
      });

      // Obtener progreso de cada lección
      const progressPromises = Array.from(uniqueLessons.values()).map(async ({ unit, lesson }) => {
        const progress = await this.getUserLessonProgress(email, level, unit, lesson);
        
        
        // Una lección está en progreso solo si tiene intentos reales (no solo inicialización)
        let inProgress = false;
        if (progress && !progress.all_objectives_completed) {
          // Verificar si hay algún objetivo con intentos reales
          const hasRealProgress = Object.values(progress.objectives_status).some((status: any) => 
            (status.correct_count > 0) ||
            (status.total_correct > 0)
          );
          inProgress = hasRealProgress;
        }
        
        // Verificar hasEvaluation: verificar rubric_evaluation (no null, undefined, o objeto vacío)
        let hasEvaluation = false;
        if (progress?.rubric_evaluation) {
          const evaluation = progress.rubric_evaluation;
          // Si es un objeto, verificar que tenga propiedades
          if (typeof evaluation === 'object' && evaluation !== null) {
            hasEvaluation = Object.keys(evaluation).length > 0;
          } else if (evaluation !== null && evaluation !== undefined && evaluation !== '') {
            hasEvaluation = true;
          }
        }
        
        // Si no hay rubric_evaluation pero hay final_score, también considerar que tiene evaluación
        if (!hasEvaluation && progress?.final_score !== null && progress?.final_score !== undefined) {
          hasEvaluation = true;
        }
        
        // Si la lección está completada y tiene completion_date, considerar que tiene evaluación
        // (esto cubre casos donde la evaluación no se guardó pero la lección se completó)
        if (!hasEvaluation && progress?.all_objectives_completed && progress?.completion_date) {
          hasEvaluation = true;
        }
        
        const result = {
          unit,
          lesson,
          completed: progress?.all_objectives_completed || false,
          inProgress,
          completionDate: progress?.completion_date || undefined,
          unlocked: false, // Se calculará después
          hasEvaluation,
        };
        
        
        return result;
      });

      const progressResults = await Promise.all(progressPromises);
      
      // Calcular qué lecciones están desbloqueadas
      // Ordenar por unidad y lección
      progressResults.sort((a, b) => {
        if (a.unit !== b.unit) return a.unit - b.unit;
        return a.lesson - b.lesson;
      });
      
      // La primera lección siempre está desbloqueada
      if (progressResults.length > 0) {
        progressResults[0].unlocked = true;
      }
      
      // Para cada lección, verificar si la anterior está completada
      for (let i = 1; i < progressResults.length; i++) {
        const current = progressResults[i];
        const previous = progressResults[i - 1];
        
        // Si la lección actual ya está en progreso o completada, está desbloqueada
        if (current.inProgress || current.completed) {
          current.unlocked = true;
          continue;
        }
        
        // Si están en la misma unidad, la anterior debe estar completada
        if (current.unit === previous.unit) {
          current.unlocked = previous.completed;
        } else {
          // Si cambió de unidad, la última lección de la unidad anterior debe estar completada
          // Buscar la última lección de la unidad anterior
          const previousUnitLessons = progressResults.filter(p => p.unit === previous.unit);
          const previousUnitLastLesson = previousUnitLessons.length > 0
            ? previousUnitLessons.sort((a, b) => b.lesson - a.lesson)[0]
            : null;
          
          current.unlocked = previousUnitLastLesson?.completed || false;
        }
      }
      
      return progressResults;
    } catch (error) {
      // console.error('[AcademicService] Error al obtener progreso del nivel:', error);
      return [];
    }
  }

  /**
   * Encuentra la lección más nueva desbloqueada o en progreso
   * Retorna la lección más avanzada que esté desbloqueada o en progreso
   */
  findLatestUnlockedLesson(levelProgress: LevelProgress[]): { unit: number; lesson: number } | null {
    if (levelProgress.length === 0) {
      return null;
    }

    // Ordenar por unidad y lección (más nueva primero)
    const sorted = [...levelProgress].sort((a, b) => {
      if (a.unit !== b.unit) return b.unit - a.unit; // Unidad más alta primero
      return b.lesson - a.lesson; // Lección más alta primero
    });

    // Buscar la primera lección que esté desbloqueada o en progreso
    const latest = sorted.find(p => p.unlocked || p.inProgress || p.completed);
    
    if (latest) {
      return { unit: latest.unit, lesson: latest.lesson };
    }

    // Si no hay ninguna desbloqueada, retornar la primera lección (siempre está desbloqueada)
    const first = sorted[sorted.length - 1]; // La última en orden ascendente es la primera
    return { unit: first.unit, lesson: first.lesson };
  }

  /**
   * Obtiene o genera una tarea personalizada para un estudiante y lección
   */
  async getPersonalizedTask(
    email: string,
    level: string,
    unit: number,
    lesson: number,
    userAge?: number,
    userCity?: string,
    additionalVariables?: Record<string, any>
  ): Promise<string | null> {
    try {
      return await personalizedTaskService.getOrGenerateTask(
        email,
        level,
        unit,
        lesson,
        userAge,
        userCity,
        additionalVariables
      );
    } catch (error) {
      console.error('[AcademicService] Error al obtener tarea personalizada:', error);
      return null;
    }
  }

  /**
   * Actualiza campos específicos de user_lesson_progress
   */
  async updateUserLessonProgress(
    email: string,
    level: string,
    unit: number,
    lesson: number,
    updates: Partial<UserLessonProgress>
  ): Promise<UserLessonProgress | null> {
    try {
      const { data, error } = await supabaseService
        .getClient()
        .from('user_lesson_progress')
        .update(updates)
        .eq('user_email', email)
        .eq('level', level)
        .eq('unit', unit)
        .eq('lesson', lesson)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data as UserLessonProgress;
    } catch (error) {
      console.error('[AcademicService] Error al actualizar progreso:', error);
      return null;
    }
  }

  /**
   * Crea un nuevo registro de user_lesson_progress
   */
  async createUserLessonProgress(
    email: string,
    level: string,
    unit: number,
    lesson: number,
    initialData: Partial<UserLessonProgress>
  ): Promise<UserLessonProgress | null> {
    try {
      const newProgress: Partial<UserLessonProgress> = {
        user_email: email,
        level,
        unit,
        lesson,
        objectives_status: initialData.objectives_status || {},
        personalized_task: initialData.personalized_task,
        all_objectives_completed: false,
        attempts: 0,
        ...initialData,
      };

      const { data, error } = await supabaseService
        .getClient()
        .from('user_lesson_progress')
        .insert([newProgress])
        .select()
        .single();

      if (error) {
        const isConflict = (error as any).code === '23505' || (error as any).status === 409;
        if (isConflict) {
          const { data: existing } = await supabaseService
            .getClient()
            .from('user_lesson_progress')
            .select('*')
            .eq('user_email', email)
            .eq('level', level)
            .eq('unit', unit)
            .eq('lesson', lesson)
            .maybeSingle();
          if (existing) return existing as UserLessonProgress;
        }
        throw error;
      }

      return data as UserLessonProgress;
    } catch (error) {
      // console.error('[AcademicService] Error al crear progreso:', error);
      return null;
    }
  }

  /**
   * Iniciar actividad del profesor: convertir a formato académico y activar modo académico
   * Convierte ActivityObjective[] a LessonObjective[] y activa el modo académico
   */
  async startTeacherActivity(
    activity: TeacherActivity,
    userEmail: string,
    onObjectiveUpdate?: (objectiveDescription: string, isCorrect: boolean) => void,
    onEvaluationComplete?: () => void
  ): Promise<void> {
    try {
      const level = activity.tache_id ? `ACTIVIDAD_${activity.tache_id}` : 'ACTIVIDAD';
      const unit = 0; // Las actividades del profesor no tienen unit/lesson específicos
      const lesson = 0;

      // Convertir ActivityObjective[] a LessonObjective[]
      const lessonObjectives: LessonObjective[] = activity.objectives.map((obj, index) => ({
        id: `teacher-activity-${activity.id}-${index}`,
        level: level,
        unit: unit,
        lesson: lesson,
        description: obj.description,
        order_index: index,
      }));

      // Crear o actualizar el progreso de la lección con los objetivos de la actividad del profesor
      // Esto asegura que los objetivos estén disponibles cuando se intente actualizar su estado
      logger.log('[AcademicService] 🔍 Buscando progreso para actividad del profesor:', {
        userEmail,
        level,
        unit,
        lesson,
        objectivesCount: activity.objectives.length
      });
      
      let progress = await this.getUserLessonProgress(userEmail, level, unit, lesson);
      
      if (!progress) {
        // Si no existe, crear el progreso con los objetivos de la actividad
        const initialStatus: Record<string, ObjectiveStatus> = {};
        
        activity.objectives.forEach(obj => {
          initialStatus[obj.description] = {
            completed: false,
            correct_count: 0,
            total_correct: 0,
            used_phrases: [],
          };
        });

        logger.log('[AcademicService] 📝 Creando progreso con objetivos:', {
          level,
          unit,
          lesson,
          objectivesStatus: Object.keys(initialStatus),
          initialStatus
        });

        progress = await this.createUserLessonProgress(
          userEmail,
          level,
          unit,
          lesson,
          {
            objectives_status: initialStatus,
            personalized_task: activity.task_description,
          }
        );

        if (!progress) {
          console.error('[AcademicService] Error al crear progreso para actividad del profesor');
          throw new Error('No se pudo crear el progreso de la actividad');
        }
        
        logger.log('[AcademicService] ✅ Progreso creado exitosamente:', {
          id: progress.id,
          objectivesStatus: Object.keys(progress.objectives_status || {})
        });
      } else {
        // Si ya existe, asegurar que todos los objetivos de la actividad estén en el progreso
        const needsUpdate = activity.objectives.some(obj => 
          !progress!.objectives_status || !progress!.objectives_status[obj.description]
        );

        if (needsUpdate) {
          const updatedStatus = { ...progress.objectives_status };
          
          activity.objectives.forEach(obj => {
            if (!updatedStatus[obj.description]) {
              updatedStatus[obj.description] = {
                completed: false,
                correct_count: 0,
                total_correct: 0,
                used_phrases: [],
              };
            }
          });

          await this.updateUserLessonProgress(
            userEmail,
            level,
            unit,
            lesson,
            {
              objectives_status: updatedStatus,
              personalized_task: activity.task_description,
            }
          );
        }
      }

      // Activar modo académico en webSocketService (incl. callback para refrescar UI cuando termine la evaluación)
      webSocketService.setAcademicMode(
        level,
        unit,
        lesson,
        lessonObjectives,
        userEmail,
        onObjectiveUpdate,
        activity.task_description, // Usar task_description como personalizedTask
        onEvaluationComplete
      );

      // Iniciar la actividad (prompt ruta académica + WebSocket); ahí se guarda el estado "En progreso"
      await webSocketService.startLessonActivity();
    } catch (error) {
      console.error('[AcademicService] Error al iniciar actividad del profesor:', error);
      throw error;
    }
  }
}

export const academicService = new AcademicService();
export default academicService;

